/**
 * Affiliate portal UI className fragments + brand tokens.
 *
 * Keep these local to the affiliate portal so we can tune contrast/UX without
 * changing global design-system defaults.
 *
 * Brand rule: accents use purple (left) → pink (right). Navbar/bars may keep a
 * soft fuchsia midpoint for richness; CTAs are a clean two-stop purple → pink.
 *
 * IMPORTANT: Never put these gradient stops on design-system `PrimaryButton`.
 * The repo Tailwind config sets `important: true`, so PrimaryButton's
 * `from-blue-600` / `to-sky-500` win by CSS source order and produce a broken
 * blue → fuchsia → sky button. Use `AffiliatePrimaryButton` instead.
 */

/** Tailwind gradient stop classes — single source of truth for the portal brand. */
export const affiliateBrandFrom = "from-purple-700";
export const affiliateBrandVia = "via-fuchsia-600";
export const affiliateBrandTo = "to-pink-500";
/** Three-stop (navbar / accent bars). */
export const affiliateBrandGradientStops = `${affiliateBrandFrom} ${affiliateBrandVia} ${affiliateBrandTo}`;
/** Two-stop CTA: purple left → pink right. */
export const affiliateBrandButtonGradientStops = `${affiliateBrandFrom} ${affiliateBrandTo}`;

export const affiliateBrandFromHover = "hover:from-purple-800";
export const affiliateBrandViaHover = "hover:via-fuchsia-700";
export const affiliateBrandToHover = "hover:to-pink-600";
export const affiliateBrandGradientHoverStops = `${affiliateBrandFromHover} ${affiliateBrandViaHover} ${affiliateBrandToHover}`;
export const affiliateBrandButtonGradientHoverStops = `${affiliateBrandFromHover} ${affiliateBrandToHover}`;

/** Hex values matching the Tailwind stops above (for charts / inline styles). */
export const affiliateBrandColors = {
  purple: "#7e22ce", // purple-700
  fuchsia: "#c026d3", // fuchsia-600
  pink: "#ec4899", // pink-500
};

export const affiliateHighContrastButtonText = "dark:!text-white";
export const affiliateButtonLabel = "whitespace-nowrap";

export const affiliateNavbarGradient = `bg-gradient-to-r ${affiliateBrandGradientStops} text-white shadow-lg border-b border-white/10`;

export const affiliateAccentTabActive = `bg-gradient-to-r ${affiliateBrandGradientStops} text-white shadow-sm hover:opacity-95`;

/** CTA fill — only safe when no competing from-/to- classes are on the element. */
export const affiliateAccentGradientButton = [
  "bg-gradient-to-r",
  affiliateBrandButtonGradientStops,
  affiliateBrandButtonGradientHoverStops,
  "text-white shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all",
  "focus:ring-pink-400 dark:focus:ring-pink-300",
].join(" ");

/** Thin accent bar (e.g. top of stat cards). */
export const affiliateBrandBar = `bg-gradient-to-r ${affiliateBrandGradientStops}`;

/** Gradient text in brand purple → fuchsia → pink (matches navbar). */
export const affiliateBrandGradientText = `bg-gradient-to-r ${affiliateBrandGradientStops} text-transparent bg-clip-text`;

/** Soft brand surface for selected/open controls (chevron, chips). */
export const affiliateAccentSoftActive =
  "bg-purple-700/10 border-purple-300 dark:border-pink-700 text-purple-800 dark:text-pink-300";

export const affiliateAccentIconButton =
  "w-7 h-7 inline-flex items-center justify-center rounded-md bg-purple-700/10 dark:bg-pink-500/25 text-purple-800 dark:!text-white hover:bg-purple-700/20 dark:hover:bg-pink-500/35 focus:outline-none focus:ring-2 focus:ring-pink-400";

export const affiliateAccentTextAction =
  "text-purple-800 dark:!text-pink-300 hover:!text-pink-700 dark:hover:!text-pink-200";

export const affiliateSpinnerBorder = "border-purple-700 dark:border-pink-400";

/**
 * Secondary affiliate action: pale surface, purple→pink gradient border, purple
 * text. Used for the per-coupon actions (Copy / Send) so they read as supporting
 * actions next to the page's solid gradient primaries.
 *
 * The gradient border is drawn with a padded gradient background plus an inner
 * pale layer, because CSS cannot gradient a `border-color` directly.
 */
export const affiliateSecondaryButtonWrapper = `bg-gradient-to-r ${affiliateBrandButtonGradientStops} p-px rounded-lg`;
export const affiliateSecondaryButtonInner =
  "flex items-center justify-center w-full h-full rounded-[7px] bg-white dark:bg-dark-800 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-dark-700 transition-colors";
